import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, CircularProgress, Dialog, useTheme } from "@mui/material";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { CalendarDaysIcon, CalendarIcon, VideoCameraIcon } from "@heroicons/react/24/outline";
import { ToastContainer, Zoom, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  CALENDAR_PROVIDERS,
  INTEGRATION_STATUS,
  describeCalendarError,
  disconnectIntegration,
  fetchCalendarConnections,
  getErrorMessage,
  getIntegrationConnectUrl,
  getMicrosoftAdminConsentUrl,
  logoutIfSessionExpired,
  microsoftReturnMessage,
} from "../../utils/interviewApi";

// When a sign-in was last opened in another tab. Coming back to this tab re-checks the connections.
let lastConnectStartedAt = 0;
const RECHECK_WINDOW_MS = 15 * 60 * 1000;
const connectRecentlyStarted = () => Date.now() - lastConnectStartedAt < RECHECK_WINDOW_MS;

export function useCalendarConnections(active = true) {
  const [connections, setConnections] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setConnections(await fetchCalendarConnections());
    } catch (err) {
      if (logoutIfSessionExpired(err)) return;
      setError(getErrorMessage(err, "Could not check calendar connections."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    refresh();
    return undefined;
  }, [active, refresh]);

  useRecheckOnReturn(active, refresh);

  return { connections, error, loading, refresh };
}

// Runs onReturn when the person comes back to this tab after opening a sign-in in a new one
export function useRecheckOnReturn(active, onReturn) {
  useEffect(() => {
    if (!active) return undefined;
    const handleReturn = () => {
      if (document.visibilityState === "visible" && connectRecentlyStarted()) onReturn();
    };
    window.addEventListener("focus", handleReturn);
    document.addEventListener("visibilitychange", handleReturn);
    return () => {
      window.removeEventListener("focus", handleReturn);
      document.removeEventListener("visibilitychange", handleReturn);
    };
  }, [active, onReturn]);
}

// Google and Microsoft sign-in open in a new tab. The tab is opened during the click, before any
// await, so popup blockers allow it; the sign-in address is loaded into it once it arrives.
export async function startProviderConnect(provider) {
  const tab = window.open("", "_blank");
  try {
    const authorizationUrl = await getIntegrationConnectUrl(provider);
    if (!authorizationUrl) {
      throw new Error("Could not start the calendar connection.");
    }
    if (!tab) {
      // The browser blocked the new tab, so sign in here instead
      window.location.href = authorizationUrl;
      return;
    }
    tab.opener = null;
    tab.location.href = authorizationUrl;
    lastConnectStartedAt = Date.now();
  } catch (err) {
    tab?.close();
    throw err;
  }
}

export const NEW_TAB_HINT = "Finish signing in on the new tab. This updates when you come back.";

export function AdminConsentOffer() {
  const theme = useTheme();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const loadLink = async () => {
    setLoading(true);
    setError("");
    setCopied(false);
    setUrl("");
    try {
      const nextUrl = await getMicrosoftAdminConsentUrl();
      if (!nextUrl) throw new Error("Could not create an admin approval link.");
      setUrl(nextUrl);
    } catch (err) {
      if (logoutIfSessionExpired(err)) return;
      setError(getErrorMessage(err, "Could not create an admin approval link."));
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setError("Could not copy the link. Select it and copy it manually.");
    }
  };

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={loadLink}
        disabled={loading}
        className="text-sm font-medium underline"
        style={{ color: theme.palette.info.main }}
      >
        {loading ? "Creating link..." : "Send this link to your Microsoft 365 admin"}
      </button>
      {url && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            readOnly
            value={url}
            aria-label="Microsoft 365 admin approval link"
            className="min-w-0 flex-1 rounded-md border px-2 py-1 text-xs"
            style={{ borderColor: theme.palette.secondary.v4 }}
            onFocus={(event) => event.target.select()}
          />
          <Button
            onClick={copyLink}
            sx={{
              textTransform: "none",
              backgroundColor: theme.palette.info.main,
              color: theme.palette.text.light,
              minWidth: "max-content",
            }}
          >
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
      )}
      {error && <p className="mt-1 text-sm text-red-700">{error}</p>}
    </div>
  );
}

function providerMeta(id) {
  return CALENDAR_PROVIDERS.find((item) => item.id === id);
}

export function ProviderConnectButtons({ providerIds = [], connections }) {
  const theme = useTheme();
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");

  const [hint, setHint] = useState("");

  const handleConnect = async (providerId) => {
    setBusyId(providerId);
    setError("");
    setHint("");
    try {
      await startProviderConnect(providerId);
      setHint(NEW_TAB_HINT);
    } catch (err) {
      if (logoutIfSessionExpired(err)) return;
      setError(describeCalendarError(err).message);
    } finally {
      setBusyId("");
    }
  };

  const ids = providerIds.filter((id) => providerMeta(id));
  if (!ids.length) return null;

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {ids.map((id) => {
          const meta = providerMeta(id);
          const status = connections?.[id]?.status;
          const label = status === INTEGRATION_STATUS.REAUTH_REQUIRED ? meta.reconnectLabel : meta.connectLabel;
          return (
            <Button
              key={id}
              onClick={() => handleConnect(id)}
              disabled={Boolean(busyId)}
              sx={{
                backgroundColor: theme.palette.info.main,
                color: theme.palette.text.light,
                textTransform: "none",
                minWidth: "max-content",
                "&:hover": { backgroundColor: "#4171cf" },
                // Keep white on blue while opening; MUI's disabled grey is unreadable here
                "&.Mui-disabled": { backgroundColor: theme.palette.info.main, color: theme.palette.text.light },
              }}
            >
              {busyId === id ? "Opening..." : label}
            </Button>
          );
        })}
      </div>
      {hint && !error && <p className="mt-2 text-sm" style={{ color: theme.palette.text.secondary }} role="status">{hint}</p>}
      {error && <p className="mt-2 text-sm text-red-700">{error}</p>}
    </div>
  );
}

const PROVIDERS = [
  { id: "google", name: "Google Calendar", shortName: "Google", Icon: CalendarIcon },
  { id: "microsoft", name: "Microsoft 365", shortName: "Microsoft", Icon: VideoCameraIcon },
];

const visibleProviders = (connections, hidden, shownConnection) =>
  PROVIDERS.filter((item) => {
    if (hidden[item.id]) return false;
    const connection = shownConnection(item.id);
    if (!connections) return true;
    return connection && connection.configured !== false;
  });

const pillStyleFor = (status, theme) => {
  if (status === INTEGRATION_STATUS.CONNECTED) {
    return { color: "#16a34a", backgroundColor: "rgba(22, 163, 74, 0.14)" };
  }
  if (status === INTEGRATION_STATUS.REAUTH_REQUIRED) {
    return { color: "#d97706", backgroundColor: "rgba(217, 119, 6, 0.14)" };
  }
  return { color: theme.palette.text.main, backgroundColor: theme.palette.secondary.v5 };
};

const statusLabelFor = (status) => {
  if (status === INTEGRATION_STATUS.CONNECTED) return "Connected";
  if (status === INTEGRATION_STATUS.REAUTH_REQUIRED) return "Reconnect required";
  return "Not connected";
};

// connectionOf includes local changes (e.g. a disconnect) made since the last fetch
function connectionSummary(providers, connections, connectionOf) {
  if (!connections) return "Checking";
  if (!providers.length) return "Not connected";
  const reconnect = providers.filter((item) => connectionOf(item.id)?.status === INTEGRATION_STATUS.REAUTH_REQUIRED);
  if (reconnect.length === 1) return `Reconnect ${reconnect[0].shortName}`;
  if (reconnect.length > 1) return "Reconnect needed";
  const connected = providers.filter((item) => connectionOf(item.id)?.status === INTEGRATION_STATUS.CONNECTED);
  if (connected.length === providers.length && connected.length > 1) return "All connected";
  if (connected.length === 1) return `${connected[0].shortName} connected`;
  if (connected.length > 1) return `${connected.length} connected`;
  return "Not connected";
}

function IntegrationRow({ provider, name, Icon, connection, onChange }) {
  const theme = useTheme();
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [hint, setHint] = useState("");
  const errorId = `${provider}-integration-error`;
  const status = connection?.status;

  // A new status means the sign-in in the other tab finished
  useEffect(() => {
    setHint("");
  }, [status]);
  const isLoading = !connection;
  const isConnected = status === INTEGRATION_STATUS.CONNECTED;
  const needsReconnect = status === INTEGRATION_STATUS.REAUTH_REQUIRED;
  const actionLabel = isConnected ? "Disconnect" : needsReconnect ? "Reconnect" : "Connect";

  const handleConnect = async () => {
    setBusy("connect");
    setError("");
    setHint("");
    try {
      await startProviderConnect(provider);
      setHint(NEW_TAB_HINT);
    } catch (err) {
      if (logoutIfSessionExpired(err)) return;
      const described = describeCalendarError(err);
      if (described.hideMicrosoft || described.hideGoogle) onChange(provider, { configured: false });
      setError(described.message);
    } finally {
      setBusy("");
    }
  };

  const handleDisconnect = async () => {
    setBusy("disconnect");
    setError("");
    try {
      await disconnectIntegration(provider);
      onChange(provider, { ...connection, status: INTEGRATION_STATUS.NOT_CONNECTED, accountEmail: "" });
    } catch (err) {
      if (logoutIfSessionExpired(err)) return;
      if (err?.response?.status === 404) {
        onChange(provider, { ...connection, status: INTEGRATION_STATUS.NOT_CONNECTED, accountEmail: "" });
      } else {
        setError(describeCalendarError(err).message);
      }
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="border-b py-4 last:border-b-0" style={{ borderColor: theme.palette.secondary.v4 }}>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="flex min-w-0 items-center gap-3">
          <Icon className="h-4 w-4 shrink-0" style={{ color: theme.palette.text.secondary }} aria-hidden="true" />
          <div className="min-w-0">
            <p className="truncate text-base" style={{ color: theme.palette.text.main }}>{name}</p>
            {isConnected && connection.accountEmail && (
              <p className="truncate text-xs" style={{ color: theme.palette.text.secondary }}>{connection.accountEmail}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold"
            style={pillStyleFor(status, theme)}
          >
            <span className="sr-only">{name} status: </span>
            {isLoading && <CircularProgress size={10} sx={{ color: "inherit" }} />}
            {isLoading ? "Checking" : statusLabelFor(status)}
          </span>
          {!isLoading && (
            <Button
              onClick={isConnected ? handleDisconnect : handleConnect}
              disabled={Boolean(busy)}
              aria-label={`${actionLabel} ${name}`}
              aria-describedby={error ? errorId : undefined}
              sx={{
                textTransform: "none",
                minWidth: "max-content",
                fontSize: "0.75rem",
                lineHeight: 1.2,
                px: 1.25,
                py: 0.5,
                borderRadius: "9999px",
                // Busy buttons are disabled; keep their colours so "Opening..." stays readable
                ...(isConnected
                  ? {
                      backgroundColor: "#DC2626",
                      color: theme.palette.text.light,
                      "&:hover": { backgroundColor: "#B91C1C" },
                      "&.Mui-disabled": { color: theme.palette.text.light, opacity: 0.7 },
                    }
                  : {
                      backgroundColor: theme.palette.info.main,
                      color: theme.palette.text.light,
                      "&:hover": { backgroundColor: "#4171cf" },
                      "&.Mui-disabled": { backgroundColor: theme.palette.info.main, color: theme.palette.text.light },
                    }),
              }}
            >
              {busy ? (
                <span className="inline-flex items-center gap-1.5">
                  <CircularProgress size={12} sx={{ color: "inherit" }} />
                  {busy === "disconnect" ? "Disconnecting..." : "Opening..."}
                </span>
              ) : (
                actionLabel
              )}
            </Button>
          )}
        </div>
      </div>
      {hint && !error && (
        <p className="mt-2 text-sm" style={{ color: theme.palette.text.secondary }} role="status">{hint}</p>
      )}
      {error && (
        <p id={errorId} className="mt-2 text-sm text-red-700" role="alert">{error}</p>
      )}
    </div>
  );
}

// The Calendar & Meetings dialog: connect or disconnect Google and Microsoft. It only shows what it is given, so the
// button on the Interviews page and the "connect" prompt in the Schedule dialog can both open the same thing.
function CalendarMeetingsDialog({ open, onClose, notice, error, visible, connections, shownConnection, onChange }) {
  const theme = useTheme();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="meeting-integrations-title"
      slotProps={{
        paper: {
          sx: {
            borderRadius: "10px",
            border: `1px solid ${theme.palette.secondary.v4}`,
            m: 2,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.text.main,
          },
        },
      }}
    >
      <div className="px-5 pt-4 pb-1">
        <div className="flex items-center justify-between gap-3">
          <h2 id="meeting-integrations-title" className="text-base font-semibold">
            Calendar &amp; Meetings
          </h2>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              aria-label="Close modal"
              className="inline-flex items-center justify-center rounded-full bg-black text-white h-6 w-6 hover:bg-gray-800 transition-colors"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
        <p className="mt-3 text-xs" style={{ color: theme.palette.text.secondary }}>
          Connect your preferred service to schedule interviews and manage meeting invitations.
        </p>
        {notice && (
          <p
            className="mt-3 text-sm"
            role={notice.tone === "quiet" ? "status" : "alert"}
            style={{ color: notice.tone === "quiet" ? theme.palette.text.secondary : "#b91c1c" }}
          >
            {notice.message}
          </p>
        )}
        {notice?.offerAdmin && <AdminConsentOffer />}
        {error && (
          <p className="mt-3 text-sm text-red-700" role="alert">{error}</p>
        )}
        {visible.map((item) => (
          <IntegrationRow
            key={item.id}
            provider={item.id}
            name={item.name}
            Icon={item.Icon}
            connection={connections ? shownConnection(item.id) : null}
            onChange={onChange}
          />
        ))}
      </div>
    </Dialog>
  );
}

// The same dialog with its own connection state, for screens that do not already hold it (the Schedule dialog).
// `onChanged` runs after a connection changes here, so the caller can re-read its own copy.
export function ConnectCalendarDialog({ open, onClose, onChanged }) {
  const { connections, error } = useCalendarConnections(open);
  const [hidden, setHidden] = useState({});
  const [overrides, setOverrides] = useState({});

  useEffect(() => {
    setOverrides({});
  }, [connections]);

  const shownConnection = (providerId) => overrides[providerId] || connections?.[providerId];
  const handleChange = (providerId, nextConnection) => {
    if (nextConnection?.configured === false) setHidden((current) => ({ ...current, [providerId]: true }));
    setOverrides((current) => ({ ...current, [providerId]: nextConnection }));
    if (onChanged) onChanged();
  };

  return (
    <CalendarMeetingsDialog
      open={open}
      onClose={onClose}
      notice={null}
      error={error}
      visible={visibleProviders(connections, hidden, shownConnection)}
      connections={connections}
      shownConnection={shownConnection}
      onChange={handleChange}
    />
  );
}

export default function MeetingIntegrations() {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const { connections, error, refresh } = useCalendarConnections(true);
  const [notice, setNotice] = useState(null);
  const [hidden, setHidden] = useState({});
  const [overrides, setOverrides] = useState({});
  // The toast container renders an empty labelled region, so it is mounted only while a toast is showing
  const [toastActive, setToastActive] = useState(false);
  const handledReturn = useRef("");

  useEffect(
    () =>
      toast.onChange((item) => {
        if (item.status === "removed") setToastActive(false);
      }),
    []
  );

  // Fresh server data replaces any local changes
  useEffect(() => {
    setOverrides({});
  }, [connections]);

  useEffect(() => {
    if (!searchParams.has("microsoft")) return undefined;
    const status = searchParams.get("microsoft");
    const reason = searchParams.get("reason");
    const key = `${status}:${reason || ""}`;
    if (handledReturn.current === key) return undefined;
    handledReturn.current = key;
    const result = microsoftReturnMessage(status, reason);
    const next = new URLSearchParams(searchParams);
    next.delete("microsoft");
    next.delete("reason");
    setSearchParams(next, { replace: true });
    refresh();
    if (result.tone === "success") {
      setToastActive(true);
      toast.success(result.message);
      setNotice(null);
      return undefined;
    }
    setNotice(result);
    if (result.tone !== "quiet") setOpen(true);
    return undefined;
  }, [searchParams, setSearchParams, refresh]);

  const shownConnection = (providerId) => overrides[providerId] || connections?.[providerId];

  const handleChange = (providerId, nextConnection) => {
    if (nextConnection?.configured === false) {
      setHidden((current) => ({ ...current, [providerId]: true }));
    }
    setOverrides((current) => ({ ...current, [providerId]: nextConnection }));
  };

  const visible = visibleProviders(connections, hidden, shownConnection);

  const summary = connectionSummary(visible, connections, shownConnection);
  const summaryColor = visible.some((item) => shownConnection(item.id)?.status === INTEGRATION_STATUS.REAUTH_REQUIRED)
    ? "#b45309"
    : visible.some((item) => shownConnection(item.id)?.status === INTEGRATION_STATUS.CONNECTED)
      ? "#15803d"
      : theme.palette.text.secondary;

  return (
    <>
      {toastActive && (
        <ToastContainer
          position="bottom-center"
          autoClose={3000}
          hideProgressBar
          transition={Zoom}
          closeOnClick
          draggable
          closeButton={false}
          limit={1}
        />
      )}
      <Button
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Calendar & Meetings, ${summary}. Open calendar and meeting connections.`}
        title={`Calendar & Meetings: ${summary}`}
        sx={{
          color: theme.palette.text.main,
          textTransform: "none",
          border: `1px solid ${theme.palette.secondary.v4}`,
          backgroundColor: theme.palette.primary.main,
          minWidth: 40,
          px: 1,
          "@media (min-width: 640px)": { minWidth: "max-content", px: 2 },
          "&:hover": { backgroundColor: theme.palette.secondary.v5 },
        }}
      >
        <CalendarDaysIcon className="h-5 w-5 sm:hidden" aria-hidden="true" />
        <span
          className="absolute right-1 top-1 h-2 w-2 rounded-full sm:hidden"
          style={{ backgroundColor: summaryColor }}
          aria-hidden="true"
        />
        <span className="max-sm:hidden">Calendar &amp; Meetings</span>
        <span className="ml-2 text-xs font-normal max-sm:hidden" style={{ color: summaryColor }}>{summary}</span>
      </Button>
      <CalendarMeetingsDialog
        open={open}
        onClose={() => setOpen(false)}
        notice={notice}
        error={error}
        visible={visible}
        connections={connections}
        shownConnection={shownConnection}
        onChange={handleChange}
      />
    </>
  );
}
